import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useFloatingCoords } from "../../../hooks/useFloatingCoords";
import { useClickOutside } from "../../../hooks/useClickOutside";
import "./Popover.css";

interface Props {
  open: boolean;
  /** The element the popover anchors beneath (and that toggles it). */
  triggerRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Extra class on the floating panel for per-use sizing. */
  className?: string;
  children: React.ReactNode;
}

/**
 * A portaled, viewport-fixed popover anchored beneath a trigger. Flips above
 * when it would overflow the bottom, closes on outside-click (the trigger is
 * ignored so its own toggle wins) and on Escape.
 */
const Popover: React.FC<Props> = ({
  open,
  triggerRef,
  onClose,
  className,
  children,
}) => {
  const floatingRef = useRef<HTMLDivElement>(null);
  const coords = useFloatingCoords(triggerRef, open, floatingRef);
  useClickOutside(floatingRef, onClose, open, triggerRef);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={floatingRef}
      className={`popover-panel${className ? " " + className : ""}`}
      style={{
        position: "fixed",
        top: coords?.top ?? -9999,
        left: coords?.left ?? -9999,
        visibility: coords ? "visible" : "hidden",
      }}
      role="dialog"
    >
      {children}
    </div>,
    document.body,
  );
};

export default Popover;
